/** PDF / DOC / DOCX / JPG / PNG */
export const FEEDBACK_DOC_IMAGE_MAX_FILE_BYTES = 10 * 1024 * 1024;
/** Video (MP4, MOV, WEBM) and ZIP */
export const FEEDBACK_MEDIA_ZIP_MAX_FILE_BYTES = 25 * 1024 * 1024;
/** Absolute per-file ceiling (video / zip). */
export const FEEDBACK_MAX_FILE_BYTES = FEEDBACK_MEDIA_ZIP_MAX_FILE_BYTES;
export const FEEDBACK_MAX_TOTAL_ATTACHMENT_BYTES = 50 * 1024 * 1024;
export const FEEDBACK_MAX_FILES = 10;
/**
 * Combined attachment budget for the inline multipart request, matching the API gateway
 * body limit. Anything heavier goes to S3 through presigned PUT instead.
 */
export const FEEDBACK_MULTIPART_REQUEST_MAX_BYTES = 10 * 1024 * 1024;

export const FEEDBACK_ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/zip',
  'application/x-zip-compressed',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'video/mp4',
  'video/quicktime',
  'video/webm',
];

export const FEEDBACK_ALLOWED_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.jpg',
  '.jpeg',
  '.png',
  '.zip',
  '.mp4',
  '.mov',
  '.webm',
];

const FEEDBACK_DOC_IMAGE_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.jpg',
  '.jpeg',
  '.png',
];

export const FEEDBACK_ATTACHMENT_ACCEPT =
  '.jpg,.jpeg,.png,.pdf,.doc,.docx,.zip,.mp4,.mov,.webm';

export const FEEDBACK_ATTACHMENT_HELP_TEXT =
  'Accepted formats: JPG, JPEG, PNG, PDF, DOC, DOCX (up to 10 MB); ZIP, MP4, MOV, WEBM (up to 25 MB). Total attachments cannot exceed 50 MB.';

export const FEEDBACK_ATTACHMENT_MESSAGES = {
  unsupportedFormat:
    'Unsupported file format. Allowed formats: JPG, JPEG, PNG, PDF, DOC, DOCX, ZIP, MP4, MOV, and WEBM.',
  fileTooLarge: (filename, maxBytes = FEEDBACK_MAX_FILE_BYTES) =>
    `"${filename}" exceeds the maximum file size of ${maxBytes / (1024 * 1024)} MB.`,
  totalTooLarge:
    'Total attachment size cannot exceed 50 MB. Please remove one or more files and try again.',
  tooManyFiles: `Maximum ${FEEDBACK_MAX_FILES} files allowed per feedback submission.`,
  duplicateFilename: (filename) =>
    `A file named "${filename}" is already attached. Please choose a different file.`,
};

function getExtension(filename) {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot <= 0) {
    return '';
  }
  return filename.slice(lastDot).toLowerCase();
}

export function getFeedbackMaxFileBytes(filename) {
  const ext = getExtension(filename);
  if (FEEDBACK_DOC_IMAGE_EXTENSIONS.includes(ext)) {
    return FEEDBACK_DOC_IMAGE_MAX_FILE_BYTES;
  }
  return FEEDBACK_MEDIA_ZIP_MAX_FILE_BYTES;
}

export function isAllowedFeedbackFile(file) {
  const ext = getExtension(file.name);
  if (!ext || !FEEDBACK_ALLOWED_EXTENSIONS.includes(ext)) {
    return false;
  }
  const mime = (file.type || '').toLowerCase();
  if (mime && !FEEDBACK_ALLOWED_MIME_TYPES.includes(mime)) {
    return false;
  }
  return true;
}

export function getTotalFeedbackAttachmentBytes(files) {
  return files.reduce((sum, file) => sum + file.size, 0);
}

export function validateFeedbackFileSelection(file, existingFiles) {
  if (existingFiles.length >= FEEDBACK_MAX_FILES) {
    return { valid: false, message: FEEDBACK_ATTACHMENT_MESSAGES.tooManyFiles };
  }
  if (!isAllowedFeedbackFile(file)) {
    return { valid: false, message: FEEDBACK_ATTACHMENT_MESSAGES.unsupportedFormat };
  }
  const maxBytes = getFeedbackMaxFileBytes(file.name);
  if (file.size > maxBytes) {
    return {
      valid: false,
      message: FEEDBACK_ATTACHMENT_MESSAGES.fileTooLarge(file.name, maxBytes),
    };
  }
  const normalizedName = file.name.trim().toLowerCase();
  const isDuplicate = existingFiles.some(
    (existing) => existing.name.trim().toLowerCase() === normalizedName
  );
  if (isDuplicate) {
    return {
      valid: false,
      message: FEEDBACK_ATTACHMENT_MESSAGES.duplicateFilename(file.name),
    };
  }
  const projectedFiles = [...existingFiles, file];
  const projectedTotal = getTotalFeedbackAttachmentBytes(projectedFiles);
  if (projectedTotal > FEEDBACK_MAX_TOTAL_ATTACHMENT_BYTES) {
    return { valid: false, message: FEEDBACK_ATTACHMENT_MESSAGES.totalTooLarge };
  }
  return { valid: true };
}

export function getFeedbackFileKind(file) {
  const mime = (file?.type || '').toLowerCase();
  const ext = getExtension(file?.name || '');

  if (mime === 'application/pdf' || ext === '.pdf') {
    return 'pdf';
  }
  if (
    mime === 'application/msword' ||
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    ext === '.doc' ||
    ext === '.docx'
  ) {
    return 'word';
  }
  return 'default';
}

export function formatFeedbackFileSize(bytes) {
  return (bytes / (1024 * 1024)).toFixed(2);
}

function formatBrowserLabel(name, version) {
  const trimmedName = (name || '').trim();
  const trimmedVersion = (version || '').trim();
  if (trimmedName && trimmedVersion) {
    return `${trimmedName} v${trimmedVersion}`;
  }
  return trimmedName || trimmedVersion || 'Unknown';
}

export function getFeedbackBrowserLabel() {
  const ua = navigator.userAgent;

  if (ua.includes('Edg/')) {
    return formatBrowserLabel('Edge', ua.match(/Edg\/([\d.]+)/)?.[1] ?? '');
  }
  if (ua.includes('Firefox/')) {
    return formatBrowserLabel('Firefox', ua.match(/Firefox\/([\d.]+)/)?.[1] ?? '');
  }
  if (ua.includes('Chrome/')) {
    return formatBrowserLabel('Chrome', ua.match(/Chrome\/([\d.]+)/)?.[1] ?? '');
  }
  if (ua.includes('Safari/')) {
    return formatBrowserLabel('Safari', ua.match(/Version\/([\d.]+)/)?.[1] ?? '');
  }

  return 'Unknown';
}

const FEEDBACK_MODULE_LABELS = {
  homepage: 'Homepage',
  template: 'Template Editor',
  PublishSucess: 'Publish',
  'my-career-site': 'My Career Site',
  launch: 'Launch',
};

export function getFeedbackModuleLabel(pathname) {
  const normalized = pathname.split('?')[0].replace(/\/+$/, '');
  const segments = normalized.split('/').filter(Boolean);
  if (segments.length === 0) {
    return 'Career Portal';
  }

  const routeKey = segments[segments.length - 1];
  return (
    FEEDBACK_MODULE_LABELS[routeKey] ??
    routeKey.replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
  );
}

export function getFeedbackPageContext(pathname, href) {
  return {
    module: getFeedbackModuleLabel(pathname),
    page_url: href,
  };
}

export function splitFeedbackFilesBySize(files) {
  const multipartFiles = [];
  const presignFiles = [];
  let multipartBytes = 0;

  for (const file of files) {
    if (multipartBytes + file.size <= FEEDBACK_MULTIPART_REQUEST_MAX_BYTES) {
      multipartFiles.push(file);
      multipartBytes += file.size;
    } else {
      presignFiles.push(file);
    }
  }
  return { multipartFiles, presignFiles };
}

export async function uploadFeedbackFileToPresignedUrl(uploadUrl, file, contentType) {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  if (!response.ok) {
    throw new Error(
      `Direct upload failed for "${file.name}" (${response.status} ${response.statusText}).`
    );
  }
}
